/**
 * User profile mutation hooks for managing user account settings.
 * @module hooks/mutations/useUserProfileMutations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/lib/api/users.api';
import { queryKeys } from '../query-keys';
import { toast } from 'sonner';
import { useAuth } from '@/store/auth.store';
import type { UpdateProfileDTO, User } from '@scrambl/shared';

/**
 * Handles successful profile update by syncing cache and auth state
 */
function handleProfileUpdateSuccess(
  queryClient: ReturnType<typeof useQueryClient>,
  setAuth: (user: User, isAuthenticated: boolean, isLoading: boolean) => void,
  updatedUser: User,
  successMessage: string
): void {
  queryClient.setQueryData(queryKeys.userProfile.currentUser(), updatedUser);
  setAuth(updatedUser, true, false);
  toast.success(successMessage);
}

/**
 * Mutation hook for updating the current user's profile
 * @returns Mutation object for profile updates
 */
export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  const { user, setAuth } = useAuth();

  return useMutation({
    mutationFn: (profileData: UpdateProfileDTO) => {
      if (!user?.id) throw new Error('User ID is required');
      return usersApi.updateProfileById(user.id, profileData);
    },
    onSuccess: (updatedUser) =>
      handleProfileUpdateSuccess(queryClient, setAuth, updatedUser, 'Profile updated successfully'),
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Failed to update profile'),
  });
}

/**
 * Mutation hook for updating the current user's timezone
 * @returns Mutation object for timezone updates
 */
export function useUpdateUserTimezoneMutation() {
  const queryClient = useQueryClient();
  const { user, setAuth } = useAuth();

  return useMutation({
    mutationFn: (timezone: string) => {
      if (!user?.id) throw new Error('User ID is required');
      return usersApi.updateProfileById(user.id, { timezone });
    },
    onSuccess: (updatedUser) =>
      handleProfileUpdateSuccess(queryClient, setAuth, updatedUser, 'Timezone updated successfully'),
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Failed to update timezone'),
  });
}
